import Button from "@mui/joy/Button";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Tables } from "../../supabase/supabase";
import Polaroid from "../polaroid/Polaroid";
import Scoreboard from "./Scoreboard";
import { TriviaQuestion } from "./TriviaQuestion";

interface TriviaFinishProps {
  questions: TriviaQuestion[];
  playAgain: () => void;
  score: number;
  todayTriviaScores: Tables<"triviaScores">[];
  allTimeTriviaScores: Tables<"triviaScores">[];
  userId: number;
}

export default function TriviaFinish({
  questions,
  playAgain,
  score,
  todayTriviaScores,
  allTimeTriviaScores,
  userId,
}: TriviaFinishProps) {
  const navigate = useNavigate();
  const [showAllPhotos, setShowAllPhotos] = React.useState(false);

  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 text-blue-600 p-4">
      <div className="text-xl font-bold mb-4 text-center">
        <span>{scoreTextMapping[score].premessage} you scored</span>
        <br />
        <span className="text-4xl">{score}</span>
        <span>{score === 1 ? " point" : " points"}! </span>
        <br />
        <span>{scoreTextMapping[score].postmessage}</span>
      </div>

      <Scoreboard
        todayTriviaScores={todayTriviaScores}
        allTimeTriviaScores={allTimeTriviaScores}
        userId={userId}
      />

      <div className="text-2xl font-bold mb-4 text-center">
        Think you can do better? Try again to answer new questions!
      </div>

      <div className="w-full flex flex-row justify-around">
        {showAllPhotos && (
          <Button
            key={"explore-site"}
            onClick={() => {
              setShowAllPhotos(false);
              navigate("/");
            }}
            size="lg"
            variant="soft"
          >
            Explore the site
          </Button>
        )}

        {!showAllPhotos && (
          <Button
            key={"reveal-photos"}
            onClick={() => {
              setShowAllPhotos(true);
            }}
            size="lg"
            variant="soft"
          >
            Reveal all photos
          </Button>
        )}

        <Button
          onClick={() => {
            setShowAllPhotos(false);
            playAgain();
          }}
          size="lg"
          variant="solid"
        >
          Play again
        </Button>
      </div>

      {showAllPhotos && (
        // Wrap the photos in a fixed-height, scrollable grid so they don't
        // push/overwrite the content above when they render. Use two
        // columns on small+ screens and one column on very small screens.
        <div className="w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-auto w-[50%] translate-x-[50%] p-2">
            {questions.map((question, index) => (
              <div
                key={question.id ?? index}
                className="w-full flex justify-center"
              >
                <Polaroid
                  src={question.photo}
                  caption={question.caption}
                  background="bg-white"
                  isClickable={false}
                  size="large"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const scoreTextMapping: {
  [key: number]: { premessage: string; postmessage: string };
} = {
  0: {
    premessage: "wow...",
    postmessage: "Sorry, but you might not get a card next year...",
  },
  1: {
    premessage: "Oof,",
    postmessage: "How did you get on the mailing list?",
  },
  2: {
    premessage: "Yikes,",
    postmessage: "*One* of us must like you a lot.",
  },
  3: {
    premessage: "Okay,",
    postmessage: "Plenty of room for improvement...",
  },
  4: {
    premessage: "Not bad,",
    postmessage: "We should probably spend more time together this year.",
  },
  5: {
    premessage: "Decent,",
    postmessage: "5 rounds up to 10, right?",
  },
  6: {
    premessage: "Solid,",
    postmessage: "You're welcome at our place any time!",
  },
  7: {
    premessage: "Impressive,",
    postmessage: "Thanks for being a great friend!",
  },
  8: {
    premessage: "Great work,",
    postmessage: "You made the short list to be our next roommate!",
  },
  9: {
    premessage: "Congratulations,",
    postmessage: "Hope it's enough to stay on the leaderboard!",
  },
  10: {
    premessage: "WOW,",
    postmessage: "You're an honorary Amigo now!",
  },
};
